import { useState, useRef, useEffect } from "react";
import PrivateVaultModal from "./PrivateVaultModal";
import usePrivateVaultModal from "@hooks/usePrivateVaultModal";
import { trackEvent } from "@lib/utils";
import { submitPrivateVaultForm } from "@services/index";

// Analytics event for submission
const PRIVATE_VAULT_TELEGRAM_SUBMITTED = "private_vault_telegram_submitted";

type TPrivateVaultModalContainerProps = {
  /** Delay in milliseconds before showing the banner (default: 500ms) */
  delay?: number;
  /** Whether to enable the banner (useful for feature flags) */
  enabled?: boolean;
  /** Custom UTM sources that trigger the banner */
  eligibleUtmSources?: string[];
  /** Custom UTM campaigns that trigger the banner */
  eligibleUtmCampaigns?: string[];
};

/**
 * Container component that manages the Private Vault floating banner and popup.
 * This component should be placed in _app.tsx to be available app-wide.
 *
 * The banner triggers when:
 * 1. User is on /gain/growth-vault page
 * 2. URL contains eligible utm_source (e.g., programmatic)
 * 3. URL contains eligible utm_campaign (e.g., ADRSBL_Display_ETH_WETH_LRTs_LSTs_10k_Global)
 *
 * The banner stops showing only after user submits the form (clicks Connect now).
 */
const PrivateVaultModalContainer = ({
  delay = 500,
  enabled = true,
  eligibleUtmSources,
  eligibleUtmCampaigns,
}: TPrivateVaultModalContainerProps) => {
  const {
    isVisible,
    hideModal,
    markFormSubmitted,
    utmSource,
    utmCampaign,
    triggerType,
  } = usePrivateVaultModal({
    delay,
    eligibleUtmSources,
    eligibleUtmCampaigns,
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ref to store auto-hide timer for cleanup
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, []);

  // Handle expanding the popup
  const handleExpand = () => {
    setIsExpanded(true);
  };

  // Handle closing - collapses back to banner or hides completely after success
  const handleClose = () => {
    // Clear auto-hide timer if user manually closes
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    setIsExpanded(false);
  };

  // Handle telegram submission - returns true on success, false on failure
  const handleSubmit = async (telegramId: string): Promise<boolean> => {
    if (!telegramId.trim()) {
      return false;
    }

    setIsSubmitting(true);

    // Get additional UTM parameters from URL
    const searchParams = new URLSearchParams(window.location.search);
    const utmMedium = searchParams.get("utm_medium");

    // Track analytics event
    trackEvent(PRIVATE_VAULT_TELEGRAM_SUBMITTED, {
      telegram_id: telegramId,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      trigger_type: triggerType,
    });

    const formData: any = new FormData();
    formData.append("telegram", telegramId);

    try {
      // Submit to Google Sheets via service
      const response = await submitPrivateVaultForm(formData);

      setIsSubmitting(false);

      // Check if API call was successful
      if (response.status === 200) {
        // Mark form as submitted to prevent banner from showing again this session
        markFormSubmitted();
        // Hide everything after a delay to show success message
        autoHideTimerRef.current = setTimeout(() => {
          hideModal();
          autoHideTimerRef.current = null;
        }, 10000);
        return true;
      }

      // API returned non-200 status - show error
      return false;
    } catch (error) {
      // API call failed - show error with retry button
      // eslint-disable-next-line no-console
      console.error("Failed to submit private vault form:", error);
      setIsSubmitting(false);
      return false;
    }
  };

  if (!enabled) {
    return null;
  }

  // Handle hiding everything (including banner) after successful submission
  const handleHideCompletely = () => {
    // Clear auto-hide timer if exists
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    setIsExpanded(false);
    hideModal();
  };

  return (
    <PrivateVaultModal
      isVisible={isVisible}
      isExpanded={isExpanded}
      onClose={handleClose}
      onExpand={handleExpand}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      onHideCompletely={handleHideCompletely}
    />
  );
};

export default PrivateVaultModalContainer;
